import React from "react";
import { Link } from "react-router-dom";
import {
  Cloud,
  CheckCircle,
  TrendingUp,
  Shield,
  DollarSign,
  ArrowRight,
  Phone,
  Brain,
} from "lucide-react";

export default function NubeHibrida() {
  const caracteristicas = [
    {
      icon: DollarSign,
      titulo: "Modelo Pay-per-Use",
      descripcion:
        "Paga solo por lo que consumes, sin inversiones iniciales en hardware.",
    },
    {
      icon: Brain,
      titulo: "IA Agéntica Integrada",
      descripcion:
        "GreenLake Intelligence optimiza automáticamente recursos con inteligencia artificial.",
    },
    {
      icon: Shield,
      titulo: "Control y Seguridad",
      descripcion:
        "Mantén tus datos on-premise con el control y la seguridad que necesitas.",
    },
    {
      icon: TrendingUp,
      titulo: "Gestión desde la Nube",
      descripcion:
        "Portal unificado para administrar toda tu infraestructura desde cualquier lugar.",
    },
  ];

  const beneficios = [
    "Reducción de costos operativos hasta 40%",
    "Optimización automática con IA agéntica",
    "Implementación en semanas, no meses",
    "Capacidad de respuesta ante picos de demanda",
    "Actualizaciones automáticas sin downtime",
    "Visibilidad completa del consumo de recursos",
    "Integración con entornos multi-cloud",
  ];

  const productos = [
    {
      nombre: "GreenLake Intelligence",
      descripcion:
        "IA agéntica que optimiza y gestiona tu infraestructura automáticamente",
    },
    {
      nombre: "GreenLake for Compute",
      descripcion:
        "Servidores bajo demanda con capacidad de escalamiento automático",
    },
    {
      nombre: "GreenLake for Storage",
      descripcion:
        "Almacenamiento flash empresarial con modelo de consumo flexible",
    },
    {
      nombre: "GreenLake for Containers",
      descripcion: "Plataforma Kubernetes empresarial totalmente gestionada",
    },
    {
      nombre: "GreenLake for VMs",
      descripcion: "Infraestructura de virtualización con gestión simplificada",
    },
    {
      nombre: "GreenLake for Data Protection",
      descripcion:
        "Backup y recuperación como servicio con garantía de disponibilidad",
    },
    {
      nombre: "GreenLake for Networking",
      descripcion: "Red empresarial as-a-service con seguridad integrada",
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1639322537228-f710d846310a?q=80&w=2232)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-indigo-900/85 to-blue-900/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-allti-blue/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="text-allti-blue font-semibold">
                Infraestructura como Servicio
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Nube Híbrida + IA Agéntica
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8">
              La experiencia de la nube pública con el control y la seguridad de
              tu datacenter, optimizada con inteligencia artificial mediante HPE
              GreenLake
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contacto"
                className="bg-allti-red text-white px-8 py-4 rounded-lg hover:bg-red-700 transition-colors text-lg font-semibold inline-flex items-center justify-center"
              >
                Solicitar Demo <ArrowRight className="ml-2" />
              </Link>
              <a
                href="#productos"
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg hover:bg-white/20 transition-colors text-lg font-semibold inline-flex items-center justify-center border border-white/30"
              >
                Ver Productos
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ¿Qué es GreenLake? */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                La nube donde tú la necesitas
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Una solución de <strong>nube híbrida</strong> te permite
                aprovechar lo mejor de dos mundos: la agilidad y escalabilidad
                de la nube pública, combinada con el control, seguridad y
                cumplimiento normativo de tu infraestructura on-premise.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                En ALLTI implementamos esta estrategia mediante{" "}
                <strong>HPE GreenLake</strong>, una plataforma de TI como
                servicio con modelo de pago por uso (pay-per-use). Eliminas
                gastos de capital y pagas solo por los recursos que utilizas,
                mientras HPE gestiona mantenimiento y actualizaciones.
              </p>
              <div className="bg-blue-50 border-l-4 border-allti-blue p-6 rounded-lg">
                <p className="text-gray-800 font-semibold mb-2">
                  <Cloud className="inline mr-2 text-allti-blue" size={24} />
                  Beneficio Principal
                </p>
                <p className="text-gray-600">
                  Transforma tu modelo de costos de CAPEX a OPEX, mejorando tu
                  flujo de caja y liberando capital para inversiones
                  estratégicas.
                </p>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=2034"
                alt="HPE GreenLake Cloud Services"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Características Principales */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Características Principales
            </h2>
            <p className="text-xl text-gray-600">
              Todo lo que necesitas para modernizar tu infraestructura
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {caracteristicas.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow"
                >
                  <div className="bg-allti-blue/10 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="text-allti-blue" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {item.titulo}
                  </h3>
                  <p className="text-gray-600">{item.descripcion}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Productos GreenLake */}
      <section id="productos" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Servicios de Nube Híbrida
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Soluciones completas para cada carga de trabajo, implementadas con
              HPE GreenLake
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {productos.map((producto, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 p-8 rounded-xl hover:shadow-xl hover:border-allti-blue/50 transition-all group"
              >
                <div className="flex items-start space-x-3 mb-4">
                  <div className="bg-allti-blue/10 p-2 rounded-lg group-hover:bg-allti-blue/20 transition-colors">
                    <Cloud className="text-allti-blue" size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 flex-1">
                    {producto.nombre}
                  </h3>
                </div>
                <p className="text-gray-600">{producto.descripcion}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-20 bg-gradient-to-br from-allti-blue to-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2015"
                alt="Benefits Analytics"
                className="rounded-xl shadow-2xl"
              />
            </div>
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">
                Beneficios Comprobados
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Empresas que han implementado HPE GreenLake reportan mejoras
                significativas:
              </p>
              <ul className="space-y-4">
                {beneficios.map((beneficio, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle
                      className="text-hpe-green flex-shrink-0 mt-1"
                      size={24}
                    />
                    <span className="text-lg text-blue-50">{beneficio}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Casos de Uso */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Casos de Uso Ideales
            </h2>
            <p className="text-xl text-gray-600">
              GreenLake se adapta perfectamente a estos escenarios
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Integración Multi-Cloud
              </h3>
              <p className="text-gray-600 mb-4">
                Conecta tu infraestructura on-premise con AWS, Azure o Google
                Cloud, manteniendo control centralizado y gobernanza unificada.
              </p>
              <Link
                to="/casos-de-uso"
                className="text-allti-blue font-semibold hover:underline inline-flex items-center"
              >
                Ver casos <ArrowRight size={16} className="ml-1" />
              </Link>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                DevOps & Containers
              </h3>
              <p className="text-gray-600 mb-4">
                Acelera el desarrollo con Kubernetes empresarial totalmente
                gestionado y herramientas de CI/CD integradas.
              </p>
              <Link
                to="/casos-de-uso"
                className="text-allti-blue font-semibold hover:underline inline-flex items-center"
              >
                Ver casos <ArrowRight size={16} className="ml-1" />
              </Link>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Data Analytics & AI
              </h3>
              <p className="text-gray-600 mb-4">
                Escalabilidad on-demand para cargas de trabajo de análisis de
                datos y machine learning sin inversión inicial.
              </p>
              <Link
                to="/casos-de-uso"
                className="text-allti-blue font-semibold hover:underline inline-flex items-center"
              >
                Ver casos <ArrowRight size={16} className="ml-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Cloud className="text-allti-blue mx-auto mb-6" size={64} />
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            ¿Listo para transformar tu infraestructura?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Como canal directo de HPE, ALLTI está certificado para diseñar e
            implementar estrategias de nube híbrida con HPE GreenLake, adaptadas
            a tus necesidades específicas.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contacto"
              className="bg-allti-red text-white px-8 py-4 rounded-lg hover:bg-red-700 transition-colors text-lg font-semibold inline-flex items-center justify-center"
            >
              <Phone className="mr-2" size={20} />
              Contactar a un Experto
            </Link>
            <Link
              to="/casos-de-uso"
              className="bg-gray-100 text-gray-900 px-8 py-4 rounded-lg hover:bg-gray-200 transition-colors text-lg font-semibold inline-flex items-center justify-center"
            >
              Ver Casos de Éxito
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
